import React, { useRef, useEffect } from "react";
import * as d3 from "d3";

// Chain Visualization component with D3
const ChainVisualization = ({ 
  chains, 
  selectedNode = null,
  selectedChains = [],
  monochrome = false,
  highlightNodesByInstitution = null  // New prop to highlight specific institution nodes
}) => {
  const svgRef = useRef(null);
  
  useEffect(() => {
    if (!svgRef.current || !chains || chains.length === 0) return;

    // Set dimensions and spacing
    const chainHeight = 100;
    const height = chains.length * chainHeight + 50;
    const width = svgRef.current.clientWidth;

    // Clear previous rendering
    d3.select(svgRef.current).selectAll("*").remove();

    // Create SVG container
    const svg = d3.select(svgRef.current)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    // Define arrow marker
    svg.append("defs").append("marker")
      .attr("id", "arrowhead")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 15)
      .attr("refY", 0)
      .attr("markerWidth", 8)
      .attr("markerHeight", 8)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", monochrome ? "#aaa" : "#999");

    // Render each chain
    chains.forEach((chain, index) => {
      const isSelected = selectedChains.includes(chain.id);
      const yPosition = index * chainHeight + 40;
      
      // Chain group
      const chainGroup = svg.append("g")
        .attr("transform", `translate(0, ${yPosition})`);

      // Chain background for selection highlighting
      if (isSelected) {
        chainGroup.append("rect")
          .attr("x", 0)
          .attr("y", -30)
          .attr("width", width)
          .attr("height", 60)
          .attr("fill", "#EBF5FF")
          .attr("rx", 4);
      }

      // Chain label
      chainGroup.append("text")
        .attr("x", 10)
        .attr("y", -15)
        .attr("fill", isSelected ? "#2563EB" : monochrome ? "#888" : "#666")
        .attr("font-weight", isSelected ? "600" : "500")
        .text(`Chain ${chain.id}`);

      // Calculate spacing based on number of nodes
      const nodeCount = chain.nodes.length;
      const spacing = (width - 100) / (nodeCount + 1);
      
      // Draw links between nodes
      for (let i = 0; i < nodeCount - 1; i++) {
        chainGroup.append("line")
          .attr("x1", 50 + spacing * (i + 1))
          .attr("y1", 0)
          .attr("x2", 50 + spacing * (i + 2))
          .attr("y2", 0)
          .attr("stroke", isSelected ? "#2563EB" : monochrome ? "#aaa" : "#999")
          .attr("stroke-width", 2)
          .attr("marker-end", "url(#arrowhead)");
      }

      // Draw nodes
      chain.nodes.forEach((node, i) => {
        const xPosition = 50 + spacing * (i + 1);
        const isNodeSelected = node === selectedNode;
        // Add check for highlighted institution
        const isInstitutionHighlighted = node === highlightNodesByInstitution;
        
        let nodeFill, nodeStroke;
        
        // Updated color logic to include highlighted institution
        if (monochrome) {
          nodeFill = isInstitutionHighlighted ? "#2563EB" : "#e0e0e0";
          nodeStroke = isInstitutionHighlighted ? "#1E40AF" : "#c0c0c0";
        } else if (isInstitutionHighlighted) {
          // Priority highlight for the selected institution
          nodeFill = "#2563EB";
          nodeStroke = "#1E40AF";
        } else if (isNodeSelected) {
          nodeFill = "#2563EB";
          nodeStroke = "#1E40AF";
        } else if (isSelected) {
          nodeFill = "#BFDBFE";
          nodeStroke = "#3B82F6";
        } else {
          nodeFill = "#f0f0f0";
          nodeStroke = "#d9d9d9";
        }
        
        // Add glow effect for highlighted institution nodes
        if (isInstitutionHighlighted) {
          // Add a glow filter definition if it doesn't exist
          if (!document.getElementById("glow-effect")) {
            const defs = svg.select("defs");
            const filter = defs.append("filter")
              .attr("id", "glow-effect")
              .attr("x", "-50%")
              .attr("y", "-50%")
              .attr("width", "200%")
              .attr("height", "200%");
              
            filter.append("feGaussianBlur")
              .attr("stdDeviation", "3")
              .attr("result", "blur");
              
            filter.append("feComposite")
              .attr("in", "SourceGraphic")
              .attr("in2", "blur")
              .attr("operator", "over");
          }
        }
        
        // Node circle with animated transitions
        const circle = chainGroup.append("circle")
          .attr("cx", xPosition)
          .attr("cy", 0)
          .attr("r", isInstitutionHighlighted ? 20 : 18)
          .attr("fill", nodeFill)
          .attr("stroke", nodeStroke)
          .attr("stroke-width", isInstitutionHighlighted ? 3 : 2)
          .style("transition", "fill 0.3s, stroke 0.3s")
          .style("filter", isInstitutionHighlighted ? "url(#glow-effect)" : "none");

        // Add hover effects
        if (!monochrome || isInstitutionHighlighted) {
          circle
            .style("cursor", "pointer")
            .on("mouseover", function() {
              d3.select(this)
                .attr("r", isInstitutionHighlighted ? 22 : 20)
                .attr("stroke-width", isInstitutionHighlighted ? 4 : 3);
            })
            .on("mouseout", function() {
              d3.select(this)
                .attr("r", isInstitutionHighlighted ? 20 : 18)
                .attr("stroke-width", isInstitutionHighlighted ? 3 : 2);
            });
        }

        // Node label
        chainGroup.append("text")
          .attr("x", xPosition)
          .attr("y", 0)
          .attr("dy", ".35em")
          .attr("text-anchor", "middle")
          .attr("fill", (isNodeSelected || isInstitutionHighlighted) ? "#fff" : monochrome ? "#666" : "#444")
          .attr("font-weight", (isNodeSelected || isInstitutionHighlighted) ? "600" : "normal")
          .text(node.toUpperCase());

        // Add obligations if present
        if (chain.obligations && chain.obligations[node]) {
          const obligations = chain.obligations[node];
          let obligationText = "";
          
          if (obligations.treasury) {
            obligationText += `T:${obligations.treasury}`;
          }
          
          if (obligations.money) {
            obligationText += obligationText ? ` $${obligations.money}` : `$${obligations.money}`;
          }
          
          if (obligationText) {
            chainGroup.append("text")
              .attr("x", xPosition)
              .attr("y", 28)
              .attr("text-anchor", "middle")
              .attr("fill", monochrome ? "#777" : "#666")
              .attr("font-size", "11px")
              .text(obligationText);
          }
        }
      });
    });
  }, [chains, selectedNode, selectedChains, monochrome, highlightNodesByInstitution]);

  return (
    <div className="visualization-container bg-gray-50 rounded-lg p-2 overflow-x-auto">
      {chains && chains.length > 0 ? (
        <svg 
          ref={svgRef} 
          width="100%" 
          height="auto" 
          style={{ minHeight: "100px", minWidth: "500px" }}
        />
      ) : (
        <div className="flex justify-center items-center h-24 text-gray-500">
          No chain data available
        </div>
      )}
    </div>
  );
};

export default ChainVisualization;